import { useMutation } from "@tanstack/react-query";
import {
  Activity as ActivityIcon,
  ChevronRight,
  Download,
  Eye,
  Orbit as OrbitIcon,
  PanelRightClose,
  Pin,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { InspectorPanel } from "@/components/rail/InspectorPanel";
import { EmptyState, ErrorState, LoadingState } from "@/components/feedback";
import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { ScrollArea } from "@/components/ui/primitives";
import { keys, useConversations, useProjects, useWorkspace } from "@/hooks/queries";
import { api, queryClient, type NodeOut, type WorkspaceItem } from "@/lib/api";
import { cn, timeAgo } from "@/lib/utils";
import { useActivity, type Tracker } from "@/stores/activity";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";

type Pane = "inspect" | "pins" | "activity";

const STAGES = ["parse", "structure", "embed", "connect", "enrich"];

const PANES = [
  ["inspect", Eye, "Inspect"],
  ["pins", Pin, "Pins"],
  ["activity", ActivityIcon, "Activity"],
] as const;

export function RightRail({
  embedded,
  onClose,
}: {
  /** Rendered inside a mobile drawer: fill it; the drawer owns open/close. */
  embedded?: boolean;
  onClose?: () => void;
} = {}) {
  const [pane, setPane] = useState<Pane>("inspect");
  // Panels are popped out on demand, never pinned open — the atlas keeps the
  // space until you ask for a panel (per design: "visible only when popped out").
  const [open, setOpen] = useState(false);
  const trackers = useActivity((s) => s.trackers);
  const liveCount = trackers.filter((t) => t.status === "running" || t.status === "queued").length;
  const selection = useGraph((s) => s.selection);
  const hasSelection = selection !== null;

  // Selecting a node/edge in the atlas pops the inspector open — a click is an
  // explicit "show me this", which is exactly when the panel should appear. On
  // mobile the drawer's open state is owned by the shell (App opens it on
  // selection), so we only switch the pane here.
  useEffect(() => {
    if (selection) {
      setPane("inspect");
      if (!embedded) setOpen(true);
    }
  }, [selection, embedded]);

  // Collapsed: a quiet rail of icons. Clicking one pops that panel out.
  if (!embedded && !open) {
    return (
      <nav className="z-20 flex w-11 shrink-0 flex-col items-center gap-1 border-l border-sidebar-border py-2">
        {PANES.map(([key, Icon, label]) => (
          <Tip key={key} label={label} side="left">
            <button
              onClick={() => {
                setPane(key);
                setOpen(true);
              }}
              aria-label={label}
              className="relative grid h-8 w-8 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <Icon className="h-4 w-4" />
              {key === "activity" && liveCount > 0 && (
                <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary animate-led" />
              )}
              {key === "inspect" && hasSelection && (
                <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-primary/70" />
              )}
            </button>
          </Tip>
        ))}
      </nav>
    );
  }

  return (
    <aside
      className={cn(
        "z-20 flex shrink-0 flex-col bg-sidebar text-sidebar-foreground",
        embedded ? "h-full w-full" : "w-[380px] border-l border-sidebar-border",
      )}
    >
      <div className="flex h-12 shrink-0 items-center gap-1 border-b border-sidebar-border/60 px-2.5">
        {PANES.map(([key, Icon, label]) => (
          <button
            key={key}
            onClick={() => setPane(key)}
            className={cn(
              "relative flex items-center gap-1.5 rounded px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors",
              pane === key ? "text-primary" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
            {key === "activity" && liveCount > 0 && (
              <span className="h-1 w-1 rounded-full bg-primary animate-led" />
            )}
          </button>
        ))}
        <Tip label="Close panel" side="left">
          <Button
            size="icon"
            variant="ghost"
            className="ml-auto"
            onClick={() => (embedded ? onClose?.() : setOpen(false))}
            aria-label="Close panel"
          >
            {embedded ? <X className="h-4 w-4" /> : <PanelRightClose className="h-3.5 w-3.5" />}
          </Button>
        </Tip>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        {pane === "inspect" && <InspectorPanel />}
        {pane === "pins" && <PinsPane />}
        {pane === "activity" && <ActivityPane />}
      </ScrollArea>
    </aside>
  );
}

/** Open a pin, resilient to a deleted target: if the node 404s we drop the
 * stale pin instead of asking the canvas to focus a dead id (which used to spin
 * the focus poller and error repeatedly). Orbit pins return to the orbits tab. */
async function openPin(item: WorkspaceItem, removeStale: (id: string) => void): Promise<void> {
  if (!item.ref_id) return;
  if (item.body === "orbit") {
    useTabs.getState().activate("orbits");
    useGraph.getState().requestFocus(item.ref_id);
    return;
  }
  try {
    const node = await api.request<NodeOut>("GET", `/api/nodes/${item.ref_id}`);
    if (node.type === "document" && node.document_id) {
      useTabs.getState().openReader(node.document_id, node.label);
    } else {
      useTabs.getState().activate("graph");
      useGraph.getState().requestFocus(node.id);
    }
  } catch {
    toast.error("That pinned item was deleted", { description: "Removing the stale pin." });
    removeStale(item.id);
  }
}

/** A collapsible group of pins (the "dropdown" the user asked for). */
function PinGroup({
  label,
  Icon,
  items,
  onOpen,
  onRemove,
}: {
  label: string;
  Icon: typeof Pin;
  items: WorkspaceItem[];
  onOpen: (item: WorkspaceItem) => void;
  onRemove: (id: string) => void;
}) {
  const [open, setOpen] = useState(true);
  if (!items.length) return null;
  return (
    <li>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left hover:bg-accent"
      >
        <ChevronRight className={cn("h-3 w-3 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")} />
        <Icon className="h-3 w-3 shrink-0 text-primary/70" />
        <span className="truncate font-mono text-2xs uppercase tracking-widest text-muted-foreground">{label}</span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">{items.length}</span>
      </button>
      {open && (
        <ul className="space-y-px pb-1 pl-3">
          {items.map((item) => (
            <li
              key={item.id}
              className="group flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 hover:bg-accent"
              onClick={() => onOpen(item)}
            >
              <Pin className="h-3 w-3 shrink-0 text-primary/60" />
              <span className="truncate text-[13.5px] text-foreground">{item.title}</span>
              <span className="ml-auto font-mono text-[10px] text-muted-foreground">{timeAgo(item.created_at)}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(item.id);
                }}
                className="rounded p-0.5 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
                aria-label="Unpin"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function PinsPane() {
  const ws = useWorkspace();
  const projects = useProjects();
  const conversations = useConversations();
  const remove = useMutation({
    mutationFn: (id: string) => api.request("DELETE", `/api/workspace/items/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.workspace }),
  });
  const removeStale = (id: string) => remove.mutate(id);

  if (ws.isLoading) {
    return <LoadingState variant="skeleton" rows={4} className="p-2.5" />;
  }
  if (ws.isError) {
    return (
      <ErrorState error={ws.error} onRetry={() => void ws.refetch()} label="pins" className="p-4" />
    );
  }

  const items = ws.data ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        icon={Pin}
        title="No pins yet"
        description="Pin nodes you want close at hand."
      />
    );
  }

  // Group pins into collapsible dropdowns: orbits, then each project, then each
  // conversation, then everything unsorted.
  const orbitPins = items.filter((i) => i.body === "orbit");
  const rest = items.filter((i) => i.body !== "orbit");
  const projName = (id: string) => projects.data?.find((p) => p.id === id)?.name ?? "Project";
  const convName = (id: string) => conversations.data?.find((c) => c.id === id)?.title ?? "Conversation";
  const byProject = new Map<string, WorkspaceItem[]>();
  const byConvo = new Map<string, WorkspaceItem[]>();
  const unsorted: WorkspaceItem[] = [];
  for (const it of rest) {
    if (it.project_id) byProject.set(it.project_id, [...(byProject.get(it.project_id) ?? []), it]);
    else if (it.conversation_id) byConvo.set(it.conversation_id, [...(byConvo.get(it.conversation_id) ?? []), it]);
    else unsorted.push(it);
  }

  const open = (item: WorkspaceItem) => void openPin(item, removeStale);
  return (
    <ul className="space-y-px p-2">
      <PinGroup label="Orbits" Icon={OrbitIcon} items={orbitPins} onOpen={open} onRemove={removeStale} />
      {[...byProject.entries()].map(([id, list]) => (
        <PinGroup key={`p:${id}`} label={projName(id)} Icon={Pin} items={list} onOpen={open} onRemove={removeStale} />
      ))}
      {[...byConvo.entries()].map(([id, list]) => (
        <PinGroup key={`c:${id}`} label={convName(id)} Icon={Pin} items={list} onOpen={open} onRemove={removeStale} />
      ))}
      <PinGroup label="Unsorted" Icon={Pin} items={unsorted} onOpen={open} onRemove={removeStale} />
    </ul>
  );
}

function TrackerRow({ t }: { t: Tracker }) {
  const done = t.status === "done";
  const failed = t.status === "failed";
  return (
    <li className="rounded-md border border-border bg-muted/40 p-2.5">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            done ? "bg-viridian" : failed ? "bg-destructive" : "bg-primary animate-led",
          )}
        />
        <span className="truncate text-xs text-foreground">{t.title}</span>
        <span className="ml-auto font-mono text-[10px] uppercase text-muted-foreground">{t.kind}</span>
      </div>
      {t.kind === "ingest" && !done && !failed && (
        <div className="mt-2 flex gap-1">
          {STAGES.map((s) => {
            const st = t.stages?.[s];
            return (
              <span
                key={s}
                title={s}
                className={cn(
                  "h-1 flex-1 rounded-full",
                  st === "done" && "bg-viridian/80",
                  st === "running" && "bg-primary animate-led",
                  st === "failed" && "bg-destructive",
                  st === "skipped" && "bg-border",
                  (!st || st === "pending") && "bg-muted",
                )}
              />
            );
          })}
        </div>
      )}
      {failed && t.error && <p className="mt-1.5 text-2xs text-destructive/90">{t.error.slice(0, 140)}</p>}
      {done && t.kind === "export" && (
        <button
          onClick={() => window.open(`/api/exports/${t.id}/download`, "_blank")}
          className="mt-1.5 flex items-center gap-1 font-mono text-2xs text-primary hover:text-primary/80"
        >
          <Download className="h-3 w-3" /> download artifact
        </button>
      )}
    </li>
  );
}

function ActivityPane() {
  const trackers = useActivity((s) => s.trackers);
  if (trackers.length === 0) {
    return (
      <EmptyState icon={ActivityIcon} title="The observatory is quiet" className="p-8" />
    );
  }
  return (
    <div className="flex flex-col gap-3 p-3.5">
      <ul className="space-y-1.5">{trackers.map((t) => <TrackerRow key={t.id} t={t} />)}</ul>
    </div>
  );
}

async function _exportDocument(docId: string, title: string) {
  const { job_id } = await api.createExport({ kind: "document_markdown", document_id: docId } as never);
  useActivity.getState().upsert({ id: job_id, kind: "export", title: `export · ${title}`, status: "queued" });
  toast("Export started", { description: "Find it in Activity when it lands in your vault." });
}
export const exportDocument = _exportDocument;
